/* How often the safety-net poll re-reads the selling plan. Module-scope const,
   deliberately not a `static` class field (that would need Safari 14.1 and
   raise this file's browser floor). */
const SELLING_PLAN_POLL_MS = 1000;

class MultipleVariants extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('.multiple-variants__form');
    this.submitBtn = this.querySelector('[data-role="submit"]');
    this.items = Array.from(
      this.querySelectorAll('.multiple-variants__item')
    );

    this.moneyFormat = window.Shopify?.money_format || '${{amount}}';

    this.isSubmitting = false;

    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);

    this.submitLabel = this.submitBtn.textContent.trim();
    this.updateState();

    this.submitBtn.addEventListener('click', this.onSubmit);

    this.replaceAddToCartButton();
    this.observeSellingPlanChanges();
  }

  disconnectedCallback() {
    this.stopSellingPlanWatch();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.submitBtn.removeEventListener('click', this.onSubmit);
  }

  /* --------------------------------------------------------------------
   * SELLING-PLAN WATCH
   *
   * Recharge renders the "Subscribe & Save" / "Buy One Time" selector inside
   * the shadow root of <recharge-subscription-widget>. The 'change' event it
   * fires in there is composed:false, so it can never cross the shadow
   * boundary - which is why the old document-level 'change' listener here
   * never ran once. (It was wrong twice over: the live radio class is
   * .rc-purchase-option__input, not the .rc-radio__input it tested for.)
   *
   * What Recharge DOES do is write the chosen plan into a light-DOM hidden
   * input[name="selling_plan"] - the same input getSelectedSellingPlan() and
   * onSubmit() already read. Note that input is NOT rendered by this theme
   * (no .liquid file emits it); Recharge injects it after page load, so it
   * does not exist when this element upgrades.
   *
   * So we watch the VALUE, not the event. Three triggers, one funnel:
   *
   *   1. POLL (load-bearing). Re-reads through getSelectedSellingPlan()
   *      itself, so the detector's scope is IDENTICAL to the reader's and
   *      cannot go blind if Recharge moves the control. Also doubles as
   *      discovery for the late-injected input. Costs one property read per
   *      tick while the input is cached.
   *   2. ATTRIBUTE OBSERVER (latency only). type="hidden" puts `value` in
   *      the spec's "default" mode, so Recharge's `input.value = ...` also
   *      rewrites the value CONTENT attribute - measured live, exactly one
   *      record per toggle. Makes the repaint land in a microtask instead of
   *      up to a second later.
   *   3. DOCUMENT 'change' (future-proofing). Dead today, but the most
   *      likely way Recharge fixes its own composed:false problem is to
   *      render the radios in the light DOM - and then this is the only
   *      trigger that fires instantly. name="selling_plan" is Shopify's cart
   *      contract, not a Recharge class name.
   *
   * No Recharge tag names, class names, shadow traversal or event
   * assumptions anywhere in this block.
   * ------------------------------------------------------------------ */
  observeSellingPlanChanges() {
    if (this.sellingPlanTimer) return; // already watching (re-connect)

    this.sellingPlanInput = null;
    this.sellingPlanObserver = new MutationObserver(() => this.syncSellingPlan());

    this.onSellingPlanChange = (e) => {
      const target = e.target;
      if (!target || typeof target.closest !== 'function') return;
      if (!target.closest('[name="selling_plan"]')) return;
      this.syncSellingPlan();
    };

    // Capture phase, so we still see it if something calls stopPropagation().
    document.addEventListener('change', this.onSellingPlanChange, true);

    this.lastSellingPlan = this.readSellingPlan();
    this.sellingPlanTimer = setInterval(
      () => this.syncSellingPlan(),
      SELLING_PLAN_POLL_MS
    );

    // Tripwire: if this product offers subscriptions but no selling_plan
    // control ever appears in the light DOM, this fix is dead AND onSubmit()
    // is silently sending one-time orders. Say so instead of degrading.
    this.sellingPlanTripwire = setTimeout(() => {
      this.sellingPlanTripwire = null;

      const offersSubscription = this.items.some(
        (item) => item.dataset.sellingPlans && item.dataset.sellingPlans !== '{}'
      );

      if (!offersSubscription) return;
      if (document.querySelector('[name="selling_plan"]')) return;

      console.warn(
        '[multiple-variants] This product has selling plans, but no ' +
        '[name="selling_plan"] control exists in the light DOM 5s after load. ' +
        'Subscription prices will not refresh and onSubmit() will send no ' +
        'selling_plan. Check the Recharge subscription widget.'
      );
    }, 5000);

    this.syncSellingPlan();
  }

  stopSellingPlanWatch() {
    if (this.sellingPlanTimer) {
      clearInterval(this.sellingPlanTimer);
      this.sellingPlanTimer = null;
    }

    if (this.sellingPlanTripwire) {
      clearTimeout(this.sellingPlanTripwire);
      this.sellingPlanTripwire = null;
    }

    if (this.sellingPlanObserver) {
      this.sellingPlanObserver.disconnect();
      this.sellingPlanObserver = null;
    }

    if (this.onSellingPlanChange) {
      document.removeEventListener('change', this.onSellingPlanChange, true);
      this.onSellingPlanChange = null;
    }

    this.sellingPlanInput = null;
  }

  // Normalised so null / undefined / '' are ONE state, not three.
  readSellingPlan() {
    const cached = this.sellingPlanInput;

    if (cached && cached.isConnected) {
      return cached.value || '';
    }

    // Cold or stale: (re-)arm the fast observer, then read through the same
    // function onSubmit() uses, so the label can never disagree with the payload.
    this.bindSellingPlanInput();

    return this.getSelectedSellingPlan() || '';
  }

  bindSellingPlanInput() {
    const input = document.querySelector(
      'input[type="hidden"][name="selling_plan"]'
    );

    if (input === this.sellingPlanInput) return;

    this.sellingPlanObserver.disconnect();
    this.sellingPlanInput = input;

    if (input) {
      this.sellingPlanObserver.observe(input, {
        attributes: true,
        attributeFilter: ['value']
      });
    }
  }

  syncSellingPlan() {
    // Never repaint mid-submit. updateState() writes submitBtn.disabled, which
    // is onSubmit()'s only in-flight guard; re-enabling the button while
    // /cart/add.js is in flight lets a second click add the same items again.
    // The diff is HELD (lastSellingPlan is not advanced), so nothing is lost -
    // onSubmit()'s finally re-syncs the moment the request settles.
    if (this.isSubmitting) return;

    const current = this.readSellingPlan();
    if (current === this.lastSellingPlan) return;

    this.lastSellingPlan = current;
    this.updateState();
  }

  onClick(e) {
    const btn = e.target.closest('.qty-btn');
    if (!btn) return;

    const item = btn.closest('.multiple-variants__item');
    const input = item.querySelector('.qty-input');
    const max = parseInt(item.dataset.max, 10);
    let value = parseInt(input.value, 10) || 0;

    if (btn.dataset.action === 'increase') {
      value = Math.min(max, value + 1);
    }

    if (btn.dataset.action === 'decrease') {
      value = Math.max(0, value - 1);
    }

    if (value > 0) {
      item.classList.add('has-value');
    } else {
      item.classList.remove('has-value');
    }

    input.value = value;
    this.updateState();
  }

  onChange(e) {
    const input = e.target.closest('.qty-input');
    if (!input) return;

    const item = input.closest('.multiple-variants__item');
    const max = parseInt(item.dataset.max, 10);
    let value = parseInt(input.value, 10) || 0;

    if (value > 0) {
      item.classList.add('has-value');
    } else {
      item.classList.remove('has-value');
    }

    input.value = Math.max(0, Math.min(max, value));
    this.updateState();
  }

  updateState() {
    const totalPrice = this.calculateTotalPrice();
    const hasSelection = totalPrice > 0;

    // isSubmitting keeps the button locked for the whole in-flight window, so
    // no repaint from any source can re-open a duplicate add-to-cart.
    this.submitBtn.disabled = this.isSubmitting || !hasSelection;

    if (hasSelection) {
      this.submitBtn.textContent =
        `${this.submitLabel} · ${this.formatMoney(totalPrice)}`;
    } else {
      this.submitBtn.textContent = this.submitLabel;
    }
  }

  getSelectedSellingPlan() {
    return (
      document.querySelector('input[name="selling_plan"]:checked')?.value ||
      document.querySelector('input[type="hidden"][name="selling_plan"]')?.value ||
      document.querySelector('select[name="selling_plan"]')?.value ||
      null
    );
  }

  getVariantPrice(item) {
    const basePrice = parseInt(item.dataset.price, 10);
    const sellingPlans = JSON.parse(item.dataset.sellingPlans || '{}');
    const sellingPlanId = this.getSelectedSellingPlan();

    if (sellingPlanId && sellingPlans[sellingPlanId]) {
      return sellingPlans[sellingPlanId];
    }

    return basePrice;
  }

  replaceAddToCartButton() {
    const nativeAtcBtn = document.querySelector(
      'form[action="/cart/add"] [type="submit"], form[action="/cart/add"] [name="add"]'
    );

    if (!nativeAtcBtn) return;

    const wrapper = document.createElement('div');
    const clonedBtn = this.submitBtn.cloneNode(true);

    wrapper.style.display = 'contents';
    nativeAtcBtn.parentNode.insertBefore(wrapper, nativeAtcBtn);
    wrapper.appendChild(nativeAtcBtn);

    clonedBtn.disabled = this.submitBtn.disabled;
    clonedBtn.addEventListener('click', this.onSubmit);
    wrapper.appendChild(clonedBtn);
    nativeAtcBtn.remove();

    this.submitBtn = clonedBtn;
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    return this.moneyFormat.replace('{{amount}}', value);
  }

  calculateTotalPrice() {
    let total = 0;

    this.items.forEach(item => {
      const qty = parseInt(
        item.querySelector('.qty-input').value,
        10
      ) || 0;

      if (qty > 0) {
        const price = this.getVariantPrice(item);
        total += price * qty;
      }
    });

    return total;
  }

  async onSubmit() {
    if (this.isSubmitting) return;

    const sellingPlan = this.getSelectedSellingPlan();
    const items = [];

    this.items.forEach(item => {
      const qty = parseInt(
        item.querySelector('.qty-input').value,
        10
      );

      if (qty > 0) {
        const payload = {
          id: item.dataset.variantId,
          quantity: qty
        };

        if (sellingPlan) {
          payload.selling_plan = sellingPlan;
        }

        items.push(payload);
      }
    });

    if (!items.length) return;

    this.isSubmitting = true;
    this.submitBtn.disabled = true;

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items })
      });

      this.items.forEach(item => {
        item.querySelector('.qty-input').value = 0;
        item.classList.remove('has-value');
      });

      document.dispatchEvent(
        new CustomEvent('cart:refresh')
      );
    } finally {
      this.isSubmitting = false;
      this.lastSellingPlan = this.readSellingPlan();
      this.updateState();
    }
  }
}

customElements.define('multiple-variants', MultipleVariants);
